import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, ImageIcon, Gift, ArrowLeft, Loader2, Search, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "../lib/api";
import { GENERI, CONDIZIONI } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import BookCover, { PALETTES } from "../components/BookCover";
import ComuneCombobox from "../components/ComuneCombobox";
import IsbnScanner from "../components/IsbnScanner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function PublishBook() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    title: "", author: "", genre: "", condition: "", description: "",
    comune: user?.comune || "Fiano Romano", isbn: "", is_gift: false, photo_path: "", photo_url: "", year: "", cover_color: null,
  });
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isbnLoading, setIsbnLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const lookupIsbn = async (isbnArg) => {
    const code = (typeof isbnArg === "string" ? isbnArg : form.isbn).trim();
    if (!code) { toast.error("Inserisci un codice ISBN"); return; }
    setIsbnLoading(true);
    try {
      const { data } = await api.get(`/isbn/${encodeURIComponent(code)}`);
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        author: data.author || f.author,
        year: data.year || f.year,
        genre: data.genre || f.genre,
        photo_url: data.photo_url || f.photo_url,
        photo_path: data.photo_url ? "" : f.photo_path,
      }));
      if (data.photo_url) setPreview(data.photo_url);
      toast.success(`Dati compilati da ${data.source === "google" ? "Google Books" : "Open Library"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setIsbnLoading(false); }
  };

  const loadBook = useCallback(async () => {
    const { data } = await api.get(`/books/${id}`);
    setForm({
      title: data.title, author: data.author, genre: data.genre, condition: data.condition,
      description: data.description || "", comune: data.comune, isbn: data.isbn || "",
      is_gift: data.is_gift, photo_path: data.photo_path || "", photo_url: data.photo_url || "",
      year: data.year || "", cover_color: data.cover_color ?? null,
    });
    setPreview(fileUrl(data));
  }, [id]);

  useEffect(() => { if (isEdit) loadBook(); }, [isEdit, loadBook]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const blocked = ["pdf", "epub", "mobi", "azw", "azw3", "doc", "docx", "odt", "rtf", "txt", "djvu", "fb2", "cbz", "cbr", "lit", "xls", "xlsx", "ppt", "pptx", "zip", "rar", "7z", "csv"];
    const isImage = file.type.startsWith("image/") && ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    if (blocked.includes(ext) || !isImage) {
      toast.error("Non è consentito caricare contenuti digitali (ebook, PDF, DOC, EPUB o altri documenti). Il caricamento di opere protette da copyright viola le norme sui diritti d'autore. Carica solo un'immagine di copertina (JPG, PNG, GIF, WEBP).", { duration: 7000 });
      e.target.value = "";
      return;
    }
    setUploading(true);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_path", data.path);
      set("photo_url", "");
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPreview(null);
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.genre || !form.condition) { toast.error("Seleziona genere e condizioni"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/books/${id}`, form);
        toast.success("Annuncio aggiornato");
        navigate(`/libro/${id}`);
      } else {
        const { data } = await api.post("/books", form);
        toast.success("Libro pubblicato!");
        navigate(`/libro/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button>
      <h1 className="text-3xl font-serif font-bold tracking-tight mb-1">{isEdit ? "Modifica annuncio" : "Pubblica un libro"}</h1>
      <p className="text-muted-foreground text-sm mb-8">Condividi un libro che non usi più con la comunità.</p>

      <form onSubmit={submit} className="space-y-6">
        {/* Foto */}
        <div>
          <Label className="mb-2 block">Foto del libro</Label>
          <label className="relative flex flex-col items-center justify-center gap-2 aspect-video rounded-2xl border-2 border-dashed border-border bg-muted cursor-pointer overflow-hidden hover:border-primary/50 transition-colors">
            {preview ? (
              <img src={preview} alt="anteprima" className="absolute inset-0 w-full h-full object-cover" />
            ) : form.title ? (
              <div className="flex items-center gap-4 px-4" data-testid="generated-cover-preview">
                <div className="w-24 aspect-[3/4] rounded-lg overflow-hidden shadow-md shrink-0">
                  <BookCover book={{ title: form.title, author: form.author, cover_color: form.cover_color }} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium">Copertina generata automaticamente</p>
                  <p className="text-xs text-muted-foreground">Nessuna foto trovata. Clicca per caricare una tua immagine e sostituirla.</p>
                </div>
              </div>
            ) : (
              <>
                <ImageIcon className="w-8 h-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Trascina o clicca per caricare una foto</span>
              </>
            )}
            {uploading && <div className="absolute inset-0 bg-background/70 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
            <input type="file" accept="image/*" data-testid="photo-upload" className="hidden" onChange={onFile} />
          </label>
          {preview && <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => { setPreview(null); set("photo_path", ""); set("photo_url", ""); }}>Rimuovi foto</Button>}

          {!preview && (
            <div className="mt-3" data-testid="cover-color-picker">
              <p className="text-xs text-muted-foreground mb-2">Colore della copertina generata</p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" data-testid="cover-color-auto" onClick={() => set("cover_color", null)}
                  className={`h-8 px-3 rounded-full text-xs border transition-all ${form.cover_color === null ? "border-primary ring-2 ring-primary/30 font-medium" : "border-border text-muted-foreground"}`}>Auto</button>
                {PALETTES.map(([c1, c2], i) => (
                  <button key={i} type="button" data-testid={`cover-color-${i}`} onClick={() => set("cover_color", i)}
                    aria-label={`Colore ${i + 1}`}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${form.cover_color === i ? "ring-2 ring-offset-2 ring-primary" : ""}`}
                    style={{ background: `linear-gradient(150deg, ${c1}, ${c2})` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="t">Titolo *</Label>
            <Input id="t" data-testid="book-title" required value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 h-11" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="a">Autore *</Label>
            <Input id="a" data-testid="book-author" required value={form.author} onChange={(e) => set("author", e.target.value)} className="mt-1.5 h-11" />
          </div>
          <div>
            <Label>Genere *</Label>
            <Select value={form.genre} onValueChange={(v) => set("genre", v)}>
              <SelectTrigger data-testid="book-genre" className="mt-1.5 h-11"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{GENERI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Condizioni *</Label>
            <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger data-testid="book-condition" className="mt-1.5 h-11"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{CONDIZIONI.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Comune *</Label>
            <ComuneCombobox value={form.comune} onChange={(v) => set("comune", v)} testId="book-comune" allowAltro placeholder="Cerca comune…" nearComune={user?.comune || ""} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="year">Anno di pubblicazione</Label>
            <Input id="year" data-testid="book-year" inputMode="numeric" value={form.year} onChange={(e) => set("year", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} placeholder="Es. 2005" className="mt-1.5 h-11" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="isbn">ISBN <span className="text-muted-foreground font-normal">(facoltativo — compila i campi in automatico)</span></Label>
            <div className="flex gap-2 mt-1.5">
              <Input id="isbn" data-testid="book-isbn" value={form.isbn} onChange={(e) => set("isbn", e.target.value)} placeholder="Es. 9788804668237" className="h-11 font-mono" />
              <Button type="button" data-testid="isbn-scan" variant="outline" size="icon" onClick={() => setScannerOpen(true)} title="Scansiona il codice a barre" className="h-11 w-11 shrink-0">
                <Camera className="w-4 h-4" />
              </Button>
              <Button type="button" data-testid="isbn-lookup" variant="outline" onClick={() => lookupIsbn()} disabled={isbnLoading} className="h-11 shrink-0">
                {isbnLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" />Cerca</>}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="d">Descrizione</Label>
          <Textarea id="d" data-testid="book-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1.5" placeholder="Note sulle condizioni, edizione, dedica…" />
        </div>

        <div className="flex items-center justify-between rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <Gift className="w-5 h-5 text-rose-600" />
            <div>
              <p className="font-medium text-sm">Offrilo in regalo</p>
              <p className="text-xs text-muted-foreground">Chi lo desidera potrà chiederlo senza dare nulla in cambio.</p>
            </div>
          </div>
          <Switch data-testid="book-gift" checked={form.is_gift} onCheckedChange={(v) => set("is_gift", v)} />
        </div>

        <Button type="submit" data-testid="book-submit" disabled={saving || uploading} className="w-full h-12 bg-primary hover:bg-primary/90 text-base">
          {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Pubblica libro"}
        </Button>
      </form>

      <IsbnScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetected={(code) => { set("isbn", code); lookupIsbn(code); }}
      />
    </div>
  );
}
